import React, { useCallback, useMemo, useState } from "react"
import { makeStyles } from "tss-react/mui"
import { useNavigate } from "react-router"
import { enqueueSnackbar } from "notistack"
import { Grid, MenuItem, Skeleton, Typography } from "@mui/material"
import { MRT_ColumnDef } from "material-react-table"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import {
    SUPPLIER_ADMIN_MERCHANTS_CREATE_ROUTE,
    SUPPLIER_ADMIN_ROUTE,
    getEditSupplierMerchantRoute,
} from "../../../../../../constants/routes"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { useApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import {
    SupplierMerchantsResponse,
    deleteMerchant,
    fetchSupplierMerchantDetails,
    fetchSupplierMerchants,
    SupplierMerchantQuery,
} from "../supplierMerchantsApi"
import { Merchant } from "../supplierMerchantsTypes"
import { DeleteConfirmDialog } from "../../../../../../shared/components/dialogs/DeleteConfirmDialog"
import { useCurrentSupplier } from "../../../../../suppliers/supplierHooks"
import { StandardTwoLinesCell } from "../../../../../../shared/components/table/StandardTwoLinesCell"
import { DestructiveMenuItem } from "../../../../../../shared/components/menus/DestructiveMenuItem"

const selectSupplierMerchants = (data: SupplierMerchantsResponse) => data?.merchants || []

export function SupplierMerchants() {
    const { supplierId } = useCurrentSupplier()
    const { l10n } = useAppLocalization()
    const navigate = useNavigate()

    const [confirmModalState, setConfirmModalState] = useState<{
        showConfirmModal: boolean
        confirmModalText: string
        itemId: null | number
    }>({
        showConfirmModal: false,
        confirmModalText: "",
        itemId: null,
    })

    const surveysResult = useApiQuery(fetchSupplierMerchants, {
        queryName: SupplierMerchantQuery.LIST,
        dependencies: {
            supplierId,
        },
        select: selectSupplierMerchants,
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(surveysResult)

    const columns = useMemo<MRT_ColumnDef<Merchant>[]>(
        () => [
            columnFactory.idColumn({
                header: l10n.getString("supplier-admin-merchants-table-reg-no-column"),
                accessorKey: "caseNumber",
            }),
            columnFactory.nameColumn({
                header: l10n.getString("supplier-admin-merchants-table-merchant-column"),
                accessorKey: "name",
                Cell: ({ row }) => (
                    <StandardTwoLinesCell title={row.original.name} subtitle1={row.original.addressDisplayText} />
                ),
            }),
            columnFactory.activeInactiveStatusColumn({ header: l10n.getString("status") }, (row) => row.isActive),
        ],
        [l10n, columnFactory],
    )

    const { mutate: callDeleteMerchant } = useApiMutation(deleteMerchant, {
        async onSuccess(_, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [SupplierMerchantQuery.LIST] })
            enqueueSnackbar({
                variant: "success",
                message: data?.serviceResponse?.displayMessage || "",
            })
        },
    })

    const handleDeleteClick = useCallback(() => {
        if (confirmModalState.itemId != null) {
            callDeleteMerchant({ merchantId: confirmModalState.itemId })
        } else {
            throw new Error("merchant id is undefined")
        }
    }, [confirmModalState.itemId, callDeleteMerchant])

    return (
        <>
            <LocalizedStrict id="supplier-admin-merchants-header" attrs={{ title: true }}>
                <PageHeader
                    page={Page.SUPPLIER_MERCHANTS}
                    title="Merchants"
                    rightElement={
                        <LocalizedStrict id="supplier-admin-merchants-create-button">
                            <StandardButton onClick={() => navigate(SUPPLIER_ADMIN_MERCHANTS_CREATE_ROUTE)}>
                                Create new
                            </StandardButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="supplier-nav-item-home">
                            <BreadcrumbLink to={SUPPLIER_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="supplier-admin-nav-item-merchants">
                            <Breadcrumb>Merchants</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="supplier-admin-nav-item-merchants">
                            <Breadcrumb>Merchants</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <Grid container direction="column">
                <ApiQueryTable
                    columns={columns}
                    initialState={{ density: "spacious" }}
                    renderDetailPanel={({ row }) => {
                        return <MerchantDetails id={row.original.id} />
                    }}
                    renderRowActionMenuItems={({ row, closeMenu }) => [
                        <MenuItem
                            onClick={() => {
                                navigate(getEditSupplierMerchantRoute(row.original.id))
                            }}
                            key={`edit-${row.original.id}`}
                        >
                            {l10n.getString("supplier-admin-merchants-table-edit-button")}
                        </MenuItem>,
                        <DestructiveMenuItem
                            onClick={() => {
                                setConfirmModalState({
                                    showConfirmModal: true,
                                    confirmModalText: `${row.original.caseNumber}`,
                                    itemId: row.original.id,
                                })
                                closeMenu()
                            }}
                            key={`delete-${row.original.id}`}
                        >
                            {l10n.getString("supplier-admin-merchants-table-delete-button")}
                        </DestructiveMenuItem>,
                    ]}
                />
            </Grid>
            {confirmModalState.showConfirmModal && (
                <DeleteConfirmDialog
                    typeOfEntity={l10n.getString("supplier-admin-merchants-table-delete-modal-title")}
                    entityName={confirmModalState.confirmModalText}
                    open={confirmModalState.showConfirmModal}
                    handleClick={handleDeleteClick}
                    onClose={() =>
                        setConfirmModalState({
                            showConfirmModal: false,
                            confirmModalText: "",
                            itemId: null,
                        })
                    }
                />
            )}
        </>
    )
}

const useStylesDetails = makeStyles()(() => ({
    wrapList: {
        borderLeft: "0.25rem solid #E2E8F0",
        color: "#334155",
        // !!! The error 'Cannot read properties of undefined (reading 'slate')' occurs when using the theme
        // borderLeft: `0.25rem solid ${theme.palette.tailwind.slate[200]}`,
        // color: theme.palette.tailwind.slate[700],
        paddingLeft: "1rem",
        display: "grid",
        gap: "2rem",
        marginBottom: "2rem",
        marginTop: "1.5rem",
    },
}))

type MerchantDetailsProps = Readonly<{
    id: number
}>
export function MerchantDetails({ id }: MerchantDetailsProps) {
    const { classes } = useStylesDetails()

    const { data } = useApiQuery(fetchSupplierMerchantDetails, {
        queryName: SupplierMerchantQuery.DETAILS,
        dependencies: {
            merchantId: id,
        },
        enabled: Boolean(id),
    })

    if (data) {
        const { connectedShops, connectedCompanies, connectedDevices } = data.merchant.entityDetails
        return (
            <Grid className={classes.wrapList}>
                <Grid container flexDirection="column">
                    <LocalizedStrict id="supplier-admin-merchants-table-connected-shops">
                        <Typography variant="caption" fontWeight={500}>
                            Shops:
                        </Typography>
                    </LocalizedStrict>
                    <Typography variant="caption">{connectedShops?.map((shop) => shop.name).join(", ")}</Typography>
                </Grid>
                <Grid container flexDirection="column">
                    <LocalizedStrict id="supplier-admin-merchants-table-connected-customers">
                        <Typography variant="caption" fontWeight={500}>
                            Customers:
                        </Typography>
                    </LocalizedStrict>
                    <Typography variant="caption">
                        {connectedCompanies.map((supplier) => supplier.name).join(", ")}
                    </Typography>
                </Grid>
                <Grid container flexDirection="column">
                    <LocalizedStrict id="supplier-admin-merchants-table-connected-devices">
                        <Typography variant="caption" fontWeight={500}>
                            Devices:
                        </Typography>
                    </LocalizedStrict>
                    <Typography variant="caption">
                        {connectedDevices.map((supplier) => supplier.name).join(", ")}
                    </Typography>
                </Grid>
            </Grid>
        )
    } else
        return (
            <Grid container className={classes.wrapList} gap={0.5}>
                <Skeleton variant="rectangular" width={"100%"} height={40} />
                <Skeleton variant="rectangular" width={"100%"} height={40} />
                <Skeleton variant="rectangular" width={"100%"} height={40} />
            </Grid>
        )
}
